import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateGroup, useLeaveGroup, useDeleteGroup, useSetGroupArchived } from '@/hooks/useGroups';
import { usePremium } from '@/hooks/usePremium';
import { usePeriodSettings } from '@/hooks/usePeriodSettings';
import { hasFeatureInGroup } from '@/lib/premium';
import {
  GROUP_COLORS,
  fromMinorUnits,
  toMinorUnits,
  getMinorUnitsSync,
} from '@sharecost/shared';
import type { GroupWithMembers } from '@/api/groups';
import type { UserBalance } from '@/hooks/useBalances';
import { X, Loader2, LogOut, Trash2, Archive, ArchiveRestore } from 'lucide-react';

const THRESHOLD_PRESETS = [60, 70, 80, 90] as const;

interface EditGroupDialogProps {
  open: boolean;
  onClose: () => void;
  group: GroupWithMembers;
  userId: string;
  balances: UserBalance[] | undefined;
  onGroupRemoved: () => void;
}

export function EditGroupDialog({ open, onClose, group, userId, balances, onGroupRemoved }: EditGroupDialogProps) {
  const updateGroup = useUpdateGroup();
  const leaveGroup = useLeaveGroup();
  const deleteGroupMut = useDeleteGroup();
  const setArchived = useSetGroupArchived();
  const isArchived = group.members.find(m => m.user_id === userId)?.archived === true;

  const handleToggleArchive = async () => {
    try {
      await setArchived.mutateAsync({ groupId: group.id, archived: !isArchived });
      onClose();
      // Archiving removes the group from the active list; unarchiving puts it back.
      if (!isArchived) onGroupRemoved();
    } catch {
      // Error shown via setArchived.error
    }
  };
  const { tier } = usePremium();
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description ?? '');
  const [currency, setCurrency] = useState(group.currency_code);
  const [color, setColor] = useState(group.color ?? GROUP_COLORS[0]);

  const initialBudgetAmount = () => {
    if (group.budget_amount_minor == null) return '';
    return String(fromMinorUnits(group.budget_amount_minor, getMinorUnitsSync(group.currency_code)));
  };

  const [budgetEnabled, setBudgetEnabled] = useState(group.budget_enabled ?? false);
  const [budgetAmount, setBudgetAmount] = useState(initialBudgetAmount);
  const [budgetPeriod, setBudgetPeriod] = useState<'monthly' | 'total'>(group.budget_period ?? 'monthly');
  const [budgetThreshold, setBudgetThreshold] = useState<number>(group.budget_alert_threshold ?? 80);
  const [budgetStartDay, setBudgetStartDay] = useState<number>(group.budget_start_day ?? 1);
  // DB column defaults to true
  const [adminsCanEditExpenses, setAdminsCanEditExpenses] = useState(group.admins_can_edit_expenses ?? true);
  const { t } = useTranslation(['groups', 'settings']);
  // The admin's personal month start is the natural default for a budget that was never
  // configured (no amount yet); an existing budget keeps its day, even when it is the 1st
  const { monthStartDay: personalMonthStartDay } = usePeriodSettings();
  const budgetNeverConfigured = group.budget_amount_minor == null;
  const prefillStartDay = () => {
    if (budgetNeverConfigured && budgetStartDay === 1) {
      setBudgetStartDay(personalMonthStartDay);
    }
  };

  const canEditBudget = hasFeatureInGroup(tier, 'group_budgets', group.members);
  // FREE user can always DISABLE a previously-enabled budget as an escape hatch;
  // they just can't re-enable it or tweak its values.
  const wasEnabledInDb = group.budget_enabled === true;
  const toggleDisabled = !canEditBudget && !wasEnabledInDb;

  const handleBudgetToggle = (next: boolean) => {
    if (canEditBudget) {
      setBudgetEnabled(next);
      if (next && budgetPeriod === 'monthly') prefillStartDay();
      return;
    }
    // FREE user: only allow disable, never enable.
    if (wasEnabledInDb && !next) {
      setBudgetEnabled(false);
    }
  };

  // Reset form when dialog opens or group data changes
  useEffect(() => {
    if (open) {
      setName(group.name);
      setDescription(group.description ?? '');
      setCurrency(group.currency_code);
      setColor(group.color ?? GROUP_COLORS[0]);
      setBudgetEnabled(group.budget_enabled ?? false);
      setBudgetAmount(initialBudgetAmount());
      setBudgetPeriod(group.budget_period ?? 'monthly');
      setBudgetThreshold(group.budget_alert_threshold ?? 80);
      setBudgetStartDay(group.budget_start_day ?? 1);
      setAdminsCanEditExpenses(group.admins_can_edit_expenses ?? true);
      updateGroup.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, group]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const parsedAmount = parseFloat(budgetAmount);
    const hasValidAmount = budgetEnabled && budgetAmount.trim() !== '' && !Number.isNaN(parsedAmount) && parsedAmount > 0;

    try {
      await updateGroup.mutateAsync({
        groupId: group.id,
        data: {
          name,
          description: description || undefined,
          baseCurrency: currency,
          color,
          budgetEnabled,
          budgetAmountMinor: hasValidAmount
            ? toMinorUnits(parsedAmount, getMinorUnitsSync(currency))
            : null,
          budgetPeriod: budgetEnabled ? budgetPeriod : null,
          budgetAlertThreshold: budgetEnabled ? budgetThreshold : null,
          // Only written for an enabled monthly budget; disabling or switching to total keeps the stored day
          budgetStartDay: budgetEnabled && budgetPeriod === 'monthly' ? budgetStartDay : undefined,
          adminsCanEditExpenses,
        },
      });
      onClose();
    } catch {
      // Error is available via updateGroup.error
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md max-h-full overflow-y-auto rounded-xl bg-surface-primary p-6 shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-text-primary">{t('web:editGroupDialog.editGroup')}</h2>
          <button onClick={onClose} className="rounded-md p-1 text-text-quaternary hover:text-text-secondary">
            <X className="h-5 w-5" />
          </button>
        </div>

        {updateGroup.error && (
          <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-900/30 px-4 py-3 text-sm text-red-700 dark:text-red-400">
            {updateGroup.error instanceof Error ? updateGroup.error.message : t('web:editGroupDialog.failedToUpdateGroup')}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="edit-group-name" className="block text-sm font-medium text-text-secondary">
              {t('web:common.groupName')}
            </label>
            <input
              id="edit-group-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              required
              placeholder={t('web:common.eGTripToBarcelona')}
              className="mt-1 w-full rounded-lg border border-border-secondary px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            />
          </div>

          <div>
            <label htmlFor="edit-group-desc" className="block text-sm font-medium text-text-secondary">
              {t('groups:createModal.field.description')}
            </label>
            <input
              id="edit-group-desc"
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={t('web:common.whatIsThisGroupFor')}
              className="mt-1 w-full rounded-lg border border-border-secondary px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            />
          </div>

          <div>
            <label htmlFor="edit-group-currency" className="block text-sm font-medium text-text-secondary">
              {t('expenses:form.picker.currency')}
            </label>
            <input
              id="edit-group-currency"
              type="text"
              value={currency}
              onChange={e => setCurrency(e.target.value.toUpperCase())}
              required
              maxLength={5}
              placeholder="CZK"
              className="mt-1 w-full rounded-lg border border-border-secondary px-3 py-2 text-sm uppercase focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">{t('expenses:category.color')}</label>
            <div className="flex gap-2 flex-wrap">
              {GROUP_COLORS.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`h-8 w-8 rounded-full transition-all ${
                    color === c ? 'ring-2 ring-offset-2 ring-offset-surface-primary ring-text-quaternary scale-110' : 'hover:scale-105'
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>

          {/* Budget */}
          <div className="border-t border-border-tertiary pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-text-primary">{t('groups:settings.section.budget')}</h3>
                {!canEditBudget && (
                  <span className="rounded-md bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                    {t('web:editGroupDialog.pro')}
                  </span>
                )}
              </div>
              <label className={`inline-flex items-center gap-2 ${toggleDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                <span className="text-sm text-text-secondary">{t('web:editGroupDialog.enable')}</span>
                <input
                  type="checkbox"
                  checked={budgetEnabled}
                  disabled={toggleDisabled}
                  onChange={e => handleBudgetToggle(e.target.checked)}
                  className="h-4 w-4 rounded border-border-secondary text-brand-600 focus:ring-brand-500 disabled:opacity-50"
                />
              </label>
            </div>

            {!canEditBudget && (
              <p className="mt-1 text-xs text-text-tertiary">
                {wasEnabledInDb
                  ? t('web:editGroupDialog.budgetsAreAProFeatureYou')
                  : t('web:editGroupDialog.budgetsAreAProFeatureUpgrade')}
              </p>
            )}

            <div
              className={`mt-3 space-y-3 ${
                !canEditBudget || !budgetEnabled ? 'opacity-60 pointer-events-none' : ''
              }`}
            >
              <div>
                <label htmlFor="edit-budget-amount" className="block text-sm font-medium text-text-secondary">
                  {t('groups:settings.budget.limit', { currency })}
                </label>
                <input
                  id="edit-budget-amount"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step={getMinorUnitsSync(currency) === 0 ? '1' : '0.01'}
                  value={budgetAmount}
                  onChange={e => setBudgetAmount(e.target.value)}
                  placeholder={t('groups:settings.budget.limitPlaceholder')}
                  className="mt-1 w-full rounded-lg border border-border-secondary px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                />
              </div>

              <div>
                <span className="block text-sm font-medium text-text-secondary">{t('settings.budget.period')}</span>
                <div className="mt-1 inline-flex rounded-lg border border-border-secondary p-0.5">
                  {(['monthly', 'total'] as const).map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setBudgetPeriod(p)}
                      className={`rounded-md px-3 py-1 text-sm font-medium transition-colors ${
                        budgetPeriod === p
                          ? 'bg-brand-600 text-white'
                          : 'text-text-secondary hover:bg-surface-hover'
                      }`}
                    >
                      {p === 'monthly' ? t('settings.budget.periodMonthly') : t('settings.budget.periodTotal')}
                    </button>
                  ))}
                </div>
              </div>

              {budgetPeriod === 'monthly' && (
                <div>
                  <label htmlFor="edit-budget-start-day" className="block text-sm font-medium text-text-secondary">
                    {t('settings.budget.startDay')}
                  </label>
                  <select
                    id="edit-budget-start-day"
                    value={budgetStartDay}
                    onChange={e => setBudgetStartDay(Number(e.target.value))}
                    className="mt-1 rounded-lg border border-border-secondary bg-surface-primary pl-3 pr-10 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                  >
                    {Array.from({ length: 28 }, (_, index) => index + 1).map(day => (
                      <option key={day} value={day}>{t('settings:periods.monthStart.day', { day })}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-text-tertiary">
                    {budgetStartDay !== 1 && budgetStartDay === personalMonthStartDay
                      ? t('settings.budget.startDayPrefilled')
                      : t('settings.budget.startDayHint')}
                  </p>
                </div>
              )}

              <div>
                <span className="block text-sm font-medium text-text-secondary">{t('web:editGroupDialog.alertThreshold')}</span>
                <div className="mt-1 flex flex-wrap gap-2">
                  {THRESHOLD_PRESETS.map(v => {
                    const selected = budgetThreshold === v;
                    return (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setBudgetThreshold(v)}
                        className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                          selected
                            ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300 dark:border-brand-600'
                            : 'border-border-secondary text-text-secondary hover:bg-surface-hover'
                        }`}
                      >
                        {v}%
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Expense permissions */}
          <div className="border-t border-border-tertiary pt-4">
            <h3 className="text-sm font-semibold text-text-primary">{t('groups:settings.section.permissions')}</h3>
            <label className="mt-2 flex cursor-pointer items-start justify-between gap-3">
              <span className="min-w-0">
                <span className="block text-sm font-medium text-text-secondary">
                  {t('groups:settings.permissions.adminsCanEditExpenses.label')}
                </span>
                <span className="mt-0.5 block text-xs text-text-tertiary">
                  {adminsCanEditExpenses
                    ? t('groups:settings.permissions.adminsCanEditExpenses.descriptionOn')
                    : t('groups:settings.permissions.adminsCanEditExpenses.descriptionOff')}
                </span>
              </span>
              <input
                type="checkbox"
                checked={adminsCanEditExpenses}
                onChange={e => setAdminsCanEditExpenses(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-border-secondary text-brand-600 focus:ring-brand-500"
              />
            </label>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border border-border-secondary px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-hover"
            >
              {t('common:button.cancel')}
            </button>
            <button
              type="submit"
              disabled={updateGroup.isPending || !name.trim()}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {updateGroup.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('expenses:form.save')}
            </button>
          </div>
        </form>

        {/* Archive — neutral action, kept separate from the danger zone */}
        <div className="mt-6 border-t border-border-tertiary pt-4">
          <div className="flex items-center justify-between rounded-lg border border-border-secondary p-3">
            <div className="min-w-0 mr-3">
              <p className="text-sm font-medium text-text-primary">
                {isArchived ? t('groups:settings.archive.unarchiveTitle') : t('groups:settings.archive.title')}
              </p>
              <p className="text-xs text-text-tertiary mt-0.5">
                {isArchived
                  ? t('web:editGroupDialog.showThisGroupAgainInYour')
                  : t('web:editGroupDialog.hideThisGroupFromYourLists')}
              </p>
            </div>
            <button
              type="button"
              onClick={handleToggleArchive}
              disabled={setArchived.isPending}
              className="shrink-0 flex items-center gap-1.5 rounded-lg border border-border-secondary px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-hover disabled:opacity-50"
            >
              {setArchived.isPending
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : isArchived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
              {isArchived ? t('groups:archived.action.unarchive') : t('groups:settings.alert.archiveButton')}
            </button>
          </div>
          {setArchived.error && (
            <div className="mt-2 rounded-lg bg-red-50 dark:bg-red-900/30 px-4 py-2 text-xs text-red-700 dark:text-red-400">
              {setArchived.error instanceof Error ? setArchived.error.message : t('web:editGroupDialog.failedToUpdate')}
            </div>
          )}
        </div>

        {/* Danger Zone */}
        <DangerZone
          group={group}
          userId={userId}
          balances={balances}
          leaveGroup={leaveGroup}
          deleteGroupMut={deleteGroupMut}
          onClose={onClose}
          onGroupRemoved={onGroupRemoved}
        />
      </div>
    </div>
  );
}

function DangerZone({
  group,
  userId,
  balances,
  leaveGroup,
  deleteGroupMut,
  onClose,
  onGroupRemoved,
}: {
  group: GroupWithMembers;
  userId: string;
  balances: UserBalance[] | undefined;
  leaveGroup: ReturnType<typeof useLeaveGroup>;
  deleteGroupMut: ReturnType<typeof useDeleteGroup>;
  onClose: () => void;
  onGroupRemoved: () => void;
}) {
  const { t } = useTranslation();
  const isAdmin = group.members.some(m => m.user_id === userId && m.role === 'admin');
  const realMembers = group.members.filter(m => !m.user?.is_virtual);
  const isOnlyMember = realMembers.length === 1 && realMembers[0].user_id === userId;
  const isOnlyAdmin = isAdmin && realMembers.filter(m => m.role === 'admin').length === 1 && realMembers.length > 1;
  const myBalance = balances?.find(b => b.userId === userId);
  const hasBalance = myBalance ? myBalance.balanceMinor !== 0 : false;

  const handleLeave = async () => {
    if (!window.confirm(t('web:common.areYouSureYouWantTo'))) return;
    try {
      await leaveGroup.mutateAsync(group.id);
      onClose();
      onGroupRemoved();
    } catch {
      // Error shown via mutation state
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('web:editGroupDialog.areYouSureYouWantTo'))) return;
    try {
      await deleteGroupMut.mutateAsync(group.id);
      onClose();
      onGroupRemoved();
    } catch {
      // Error shown via mutation state
    }
  };

  return (
    <div className="mt-6 border-t border-red-200 dark:border-red-900/50 pt-4">
      <h3 className="text-sm font-semibold text-red-600 dark:text-red-400 mb-3">{t('groups:settings.section.dangerZone')}</h3>

      {(leaveGroup.error || deleteGroupMut.error) && (
        <div className="mb-3 rounded-lg bg-red-50 dark:bg-red-900/30 px-4 py-3 text-sm text-red-700 dark:text-red-400">
          {leaveGroup.error instanceof Error ? leaveGroup.error.message : ''}
          {deleteGroupMut.error instanceof Error ? deleteGroupMut.error.message : ''}
          {!(leaveGroup.error instanceof Error) && !(deleteGroupMut.error instanceof Error) && t('web:editGroupDialog.operationFailed')}
        </div>
      )}

      <div className="space-y-3">
        {/* Leave Group — hidden if only member */}
        {!isOnlyMember && (
          <div className="flex items-center justify-between rounded-lg border border-red-200 dark:border-red-900/50 p-3">
            <div className="min-w-0 mr-3">
              <p className="text-sm font-medium text-text-primary">{t('groups:settings.dangerZone.leaveButton')}</p>
              <p className="text-xs text-text-tertiary mt-0.5">
                {hasBalance
                  ? t('web:editGroupDialog.youMustSettleYourBalanceBefore')
                  : isOnlyAdmin
                    ? t('web:editGroupDialog.youAreTheOnlyAdminAssign')
                    : t('web:editGroupDialog.youWillLoseAccessToThis')}
              </p>
            </div>
            <button
              onClick={handleLeave}
              disabled={hasBalance || isOnlyAdmin || leaveGroup.isPending}
              className="shrink-0 flex items-center gap-1.5 rounded-lg border border-red-300 dark:border-red-800 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {leaveGroup.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
              {t('groups:settings.alert.leaveButton')}
            </button>
          </div>
        )}

        {/* Delete Group — admin only */}
        {isAdmin && (
          <div className="flex items-center justify-between rounded-lg border border-red-200 dark:border-red-900/50 p-3">
            <div className="min-w-0 mr-3">
              <p className="text-sm font-medium text-text-primary">{t('groups:settings.dangerZone.deleteButton')}</p>
              <p className="text-xs text-text-tertiary mt-0.5">
                {t('web:editGroupDialog.thisWillPermanentlyRemoveTheGroup')}
              </p>
            </div>
            <button
              onClick={handleDelete}
              disabled={deleteGroupMut.isPending}
              className="shrink-0 flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              {deleteGroupMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {t('common:button.delete')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
